import type { App } from '../../app.js';
import { h, button, toast } from '../dom.js';
import { VEHICLES, displayStats, VehicleDef } from '@voidline/shared';

export function buildGarageScreen(app: App): HTMLElement {
  const level = app.playerLevel();
  let sel: VehicleDef =
    VEHICLES.find((v) => v.id === app.progression.selectedVehicle) ?? VEHICLES[0];

  const list = h('div', { class: 'col', style: 'gap:8px; min-width: 300px;' });
  const detail = h('div', { class: 'col', style: 'min-width: 420px; gap: 10px; flex:1;' });

  const renderList = () => {
    list.innerHTML = '';
    for (const v of VEHICLES) {
      const locked = v.unlockLevel > level;
      list.appendChild(
        h(
          'button',
          {
            class: `focusable${v.id === sel.id ? ' selected' : ''}`,
            style: `text-align:left; padding:10px 16px; opacity:${locked ? 0.45 : 1};`,
            onclick: () => {
              if (locked) {
                toast(`Reach level ${v.unlockLevel} to unlock the ${v.name}.`, true);
                app.audio.play('ui_back');
                return;
              }
              sel = v;
              app.audio.play('ui_move');
              renderList();
              renderDetail();
            },
          },
          h(
            'div',
            { class: 'row', style: 'justify-content:space-between; width:100%;' },
            h('span', { text: v.name }),
            h('span', { class: 'subtle', style: 'font-size:10px;', text: locked ? `LV ${v.unlockLevel}` : v.archetype })
          ),
          h('div', {
            class: 'subtle',
            style: 'font-size:10px; text-transform:none; font-weight:400;',
            text: v.manufacturer,
          })
        )
      );
    }
  };

  const renderDetail = () => {
    detail.innerHTML = '';
    const selectedPaint = app.progression.selectedPaints[sel.id] ?? sel.paints[0].id;
    const stats = displayStats(sel);
    const isCurrent = app.progression.selectedVehicle === sel.id;

    detail.append(
      h('div', { class: 'h1', text: sel.name }),
      h('div', { class: 'subtle', style: 'letter-spacing:0.2em;', text: `${sel.manufacturer} — ${sel.archetype}` }),
      h('div', {
        class: 'subtle',
        style: 'text-transform:none; letter-spacing:0.03em; max-width: 46ch;',
        text: sel.description,
      }),
      h(
        'div',
        { class: 'col', style: 'gap:7px; margin-top:8px; max-width: 380px;' },
        ...stats.map((s) =>
          h(
            'div',
            {},
            h(
              'div',
              { class: 'row', style: 'justify-content:space-between; margin-bottom:3px;' },
              h('span', { class: 'subtle', style: 'font-size:11px;', text: s.label }),
              h('span', { class: 'subtle mono', style: 'font-size:11px;', text: `${Math.round(s.value * 100)}` })
            ),
            h('div', { class: 'statbar' }, h('div', { style: `width:${s.value * 100}%` }))
          )
        )
      ),
      h('div', { class: 'h2', text: 'PAINT', style: 'margin-top:10px;' }),
      h(
        'div',
        { class: 'row wrap' },
        ...sel.paints.map((p) => {
          const locked = p.unlockLevel > level;
          return h(
            'button',
            {
              class: `small focusable${p.id === selectedPaint ? ' selected' : ''}`,
              style: `opacity:${locked ? 0.4 : 1};`,
              onclick: () => {
                if (locked) {
                  toast(`Paint "${p.name}" unlocks at level ${p.unlockLevel}.`, true);
                  return;
                }
                app.progression.selectedPaints[sel.id] = p.id;
                app.saveAll();
                app.audio.play('ui_move');
                renderDetail();
              },
            },
            h('span', {
              style: `display:inline-block; width:12px; height:12px; margin-right:8px; vertical-align:middle;
                      background:${p.primary}; box-shadow: 0 0 6px ${p.glow}; border:1px solid ${p.glow};`,
            }),
            h('span', { text: locked ? `${p.name} (LV ${p.unlockLevel})` : p.name })
          );
        })
      ),
      h(
        'div',
        { class: 'row', style: 'margin-top:16px;' },
        button(
          isCurrent ? 'SELECTED' : 'SELECT THIS CRAFT',
          () => {
            app.progression.selectedVehicle = sel.id;
            app.saveAll();
            app.audio.play('ui_confirm');
            // in a lobby: update the server loadout live
            if (app.roomState && app.conn?.connected) {
              app.conn.send({
                type: 'setLoadout',
                vehicleId: sel.id,
                paintId: app.progression.selectedPaints[sel.id] ?? sel.paints[0].id,
              });
            }
            renderDetail();
            toast(`${sel.name} is now your craft.`);
          },
          `primary focusable`
        )
      )
    );
  };

  renderList();
  renderDetail();

  return h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel', style: 'max-width: 940px; width: 92vw; max-height: 88vh; overflow-y:auto;' },
      h(
        'div',
        { class: 'row', style: 'justify-content:space-between; margin-bottom:18px;' },
        h('div', { class: 'h1', text: 'GARAGE' }),
        button('◄ BACK', () => {
          app.audio.play('ui_back');
          app.show(app.roomState ? 'lobby' : 'menu');
        }, 'small focusable')
      ),
      h('div', { class: 'row', style: 'gap:34px; align-items:flex-start;' }, list, detail)
    )
  );
}
